import React, { useEffect, useState } from 'react';
import { Activity, RotateCcw, X } from 'lucide-react';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// Sticky banner shown ONLY when the current org is a sandbox.
// Lets the demo user reset the sandbox or destroy it.
// Reset is non-destructive on the org shell — only the demo data is re-seeded.
// ─────────────────────────────────────────────────────────────────────────────
export default function SandboxBanner() {
  const { user, logout } = useAuth();
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) return;
      try {
        const { data } = await api.sandboxMe();
        if (!cancelled) setMeta(data);
      } catch { /* not a sandbox session — silent */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!meta?.is_sandbox) return null;

  const expiresAt = meta?.org?.sandbox_expires_at ? new Date(meta.org.sandbox_expires_at) : null;
  const hoursLeft = expiresAt ? Math.max(0, Math.round((expiresAt - Date.now()) / 36e5)) : meta.ttl_hours;

  const reset = async () => {
    if (!window.confirm('Reset this sandbox? Demo data will be wiped and re-seeded fresh.')) return;
    try { await api.sandboxReset(); toast.success('Sandbox reset · fresh demo data ready'); window.location.reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Reset failed'); }
  };

  const exit = async () => {
    if (!window.confirm('Destroy this sandbox and sign out?')) return;
    try { await api.sandboxDestroy(); } catch { /* ignore */ }
    logout();
    window.location.href = '/';
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200" data-testid="sandbox-banner">
      <div className="max-w-[1240px] mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3 text-[12.5px] text-amber-900">
        <div className="flex items-center gap-2 min-w-0">
          <Activity className="h-3.5 w-3.5 shrink-0" />
          <span className="font-semibold tracking-tight">Sandbox mode</span>
          <span className="hidden sm:inline text-amber-800 truncate">
            Isolated demo environment · expires in {hoursLeft}h · all changes are ephemeral and cannot affect real organizations.
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={reset} data-testid="sandbox-reset"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-amber-300 text-amber-900 text-[11.5px] font-medium hover:bg-amber-100 transition-colors">
            <RotateCcw className="h-3 w-3" /> Reset
          </button>
          <button onClick={exit} data-testid="sandbox-exit"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-900 text-white text-[11.5px] font-medium hover:bg-amber-800 transition-colors">
            <X className="h-3 w-3" /> Exit
          </button>
        </div>
      </div>
    </div>
  );
}
